import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../shared/material/material.module';
import { AdvancedSearchServiceFormComponent } from './advanced-search-service-form/advanced-search-service-form.component';
import { AdvancedSearchResourceFormComponent } from './advanced-search-resource-form/advanced-search-resource-form.component';
import { PlanStatusService } from '../shared/services/plan-status-cache.service';
import { ReactiveFormsModule } from '@angular/forms';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription, takeUntil, skip } from 'rxjs';
import { Feature } from '../shared/config/package-features.config';
import { ContainerComponent } from '../shared/container/container.component';
import { SearchWizardComponent } from './search-wizard/search-wizard.component';
import { SkeletonLoaderComponent } from '../shared/skeleton-loader/skeleton-loader.component';
import { WizardStep, SearchType } from './models/search-state.models';
import { PlanStatusInfo } from '../core/generated';

export class SearchConfig {
  searcherCpfCnpj!: string;
  profileId!: string;
  planStatus!: string;
  searchType!: SearchType;
}

interface WizardState {
  currentStep: number;
  isLoading: boolean;
  allPlans: PlanStatusInfo[];
  selectedPlan?: PlanStatusInfo;
  selectedSearchType?: SearchType;
  searchConfig?: { type: 'OFFERING' | 'DEMAND'; cpfCnpj?: string };
  steps: WizardStep[];
}

@Component({
    selector: 'app-advanced-search',
  standalone: true,
    templateUrl: './advanced-search.component.html',
    styleUrls: ['./advanced-search.component.scss'],
    imports: [
      CommonModule, 
      MaterialModule, 
      ReactiveFormsModule, 
      AdvancedSearchServiceFormComponent, 
      AdvancedSearchResourceFormComponent,
      ContainerComponent,
      SearchWizardComponent,
      SkeletonLoaderComponent
    ]
})
export class AdvancedSearchComponent implements OnInit, OnDestroy {

  cdr = inject(ChangeDetectorRef);
  planStatuseService = inject(PlanStatusService);
  route = inject(ActivatedRoute);
  deviceService = inject(DeviceDetectorService);
  
  wizardState = signal<WizardState>({
    currentStep: 0,
    isLoading: true,
    allPlans: [],
    steps: [
      { id: 'plan-selection', label: 'Seleção de Plano', icon: 'assignment', isValid: false, isComplete: false },
      { id: 'search-type', label: 'Tipo de Busca', icon: 'search', isValid: false, isComplete: false },
      { id: 'search-form', label: 'Filtros de Busca', icon: 'filter_list', isValid: false, isComplete: false }
    ]
  });
  
  isLoading = computed(() => this.wizardState().isLoading);
  allPlanStatuses = computed(() => this.wizardState().allPlans);
  selectedPlan = computed(() => this.wizardState().selectedPlan);
  selectedSearchType = computed(() => this.wizardState().selectedSearchType);
  currentSearchConfig = computed(() => this.wizardState().searchConfig);
  wizardSteps = computed(() => this.wizardState().steps);
  currentWizardStep = computed(() => this.wizardState().currentStep);
  
  selectedTypeForm = computed(() => {
    const searchType = this.selectedSearchType();
    if (!searchType) return 'SERVICE';
    return searchType.includes('SERVICE') ? 'SERVICE' : 'RESOURCE';
  });
  
  isMobile = computed(() => this.deviceService.isMobile());

  private planStatusesSub?: Subscription;
  private readonly destroy$ = new Subject<void>();

  readonly Feature = Feature;

  formTypeOptions: { key: SearchType; label: string; icon: string }[] = [
    { key: 'SERVICE_OFFERING_PF', label: 'Ofertas de Consulta', icon: 'medical_services' },
    { key: 'RESOURCE_OFFERING_PJ', label: 'Oferta de Exames', icon: 'science' },
    { key: 'SERVICE_DEMAND_PJ', label: 'Demandas de Consulta', icon: 'search' },
    { key: 'RESOURCE_DEMAND_PJ', label: 'Demandas de Exames', icon: 'manage_search' },
  ];

  async ngOnInit() {
    this.updateState({ isLoading: true });

    // skip(1) para ignorar o valor inicial vazio do BehaviorSubject
    this.planStatuseService.planStatuses$.pipe(
      skip(1),
      takeUntil(this.destroy$)
    ).subscribe((list) => {
      const filteredPlans = (list as PlanStatusInfo[] || []).filter(plan => 
        plan.cpfCnpj && this.planStatuseService.hasFeatureAccess(plan.cpfCnpj, Feature.ADVANCED_SEARCH)
      );
      this.updateState({ allPlans: filteredPlans, isLoading: false });

      // Auto-seleciona se houver apenas 1 plano
      if (filteredPlans.length === 1 && !this.selectedPlan()) {
        this.selectPlan(filteredPlans[0]);
      }

      this.cdr.detectChanges();
    });
    
    await this.planStatuseService.loadForCurrentUser();

    // Após o load, verifica se a subscription já atualizou os planos
    // Se não (caso o BehaviorSubject já tinha valor antes do skip), busca manualmente
    const plans = this.allPlanStatuses();
    if (plans.length === 0) {
      const currentList = this.planStatuseService.getAllPlanStatuses() as PlanStatusInfo[];
      const filteredPlans = (currentList || []).filter(plan => 
        plan.cpfCnpj && this.planStatuseService.hasFeatureAccess(plan.cpfCnpj, Feature.ADVANCED_SEARCH)
      );
      this.updateState({ allPlans: filteredPlans, isLoading: false });

      if (filteredPlans.length === 1 && !this.selectedPlan()) {
        this.selectPlan(filteredPlans[0]);
      }
    }

    this.updateState({ isLoading: false });
    this.cdr.detectChanges();
  }

  private updateState(partial: Partial<WizardState>): void {
    this.wizardState.update(state => ({ ...state, ...partial }));
  }

  selectPlan(plan: PlanStatusInfo): void {
    const steps = [...this.wizardState().steps];
    steps[0].isValid = true;
    steps[0].isComplete = true;
    this.updateState({ selectedPlan: plan, steps });
    this.cdr.detectChanges();
  }

  selectSearchType(searchType: SearchType): void {
    const kind = searchType.includes('OFFERING') ? 'OFFERING' : 'DEMAND';
    const cpfCnpj = this.selectedPlan()?.cpfCnpj;
    const steps = [...this.wizardState().steps];
    steps[1].isValid = true;
    steps[1].isComplete = true;
    this.updateState({ 
      selectedSearchType: searchType, 
      searchConfig: { type: kind as 'OFFERING' | 'DEMAND', cpfCnpj },
      steps 
    });
    this.cdr.detectChanges();
  }

  isTypeAvailable(t: SearchType): boolean {
    const plan = this.selectedPlan();
    if (!plan || !plan.cpfCnpj) return false;
    
    const featureMap: Record<SearchType, Feature> = {
      'SERVICE_OFFERING_PF': Feature.ADVANCED_SEARCH_OFFERS_SERVICE,
      'SERVICE_OFFERING_PJ': Feature.ADVANCED_SEARCH_OFFERS_SERVICE,
      'SERVICE_DEMAND_PJ': Feature.ADVANCED_SEARCH_DEMANDS_SERVICE,
      'RESOURCE_OFFERING_PJ': Feature.ADVANCED_SEARCH_OFFERS_RESOURCE,
      'RESOURCE_DEMAND_PJ': Feature.ADVANCED_SEARCH_DEMANDS_RESOURCE,
    };

    const requiredFeature = featureMap[t];
    if (!requiredFeature) return false;

    return this.planStatuseService.hasFeatureAccess(plan.cpfCnpj, requiredFeature);
  }

  getAvailableSearchTypes(): { key: SearchType; label: string; icon: string }[] {
    return this.formTypeOptions.filter(option => this.isTypeAvailable(option.key));
  }

  onWizardStepChange(stepIndex: number): void {
    this.updateState({ currentStep: stepIndex });
    
    // Ao voltar para a etapa de seleção de plano, garante que os planos estejam carregados
    if (stepIndex === 0) {
      this.ensurePlansLoaded();
    }
    
    this.cdr.detectChanges();
  }

  /**
   * Garante que a lista de planos esteja carregada e disponível no select.
   * Chamado ao navegar de volta para a etapa 0 (Seleção de Plano).
   */
  private async ensurePlansLoaded(): Promise<void> {
    const plans = this.allPlanStatuses();
    
    // Se não há planos carregados, recarrega
    if (!plans || plans.length === 0) {
      this.updateState({ isLoading: true });
      await this.planStatuseService.loadForCurrentUser();
      this.updateState({ isLoading: false });
      this.cdr.detectChanges();
    }
    
    // Se já tinha um plano selecionado, garante que ele ainda está na lista
    const selected = this.selectedPlan();
    if (selected) {
      const currentPlans = this.allPlanStatuses();
      const stillExists = currentPlans.find(p => p.planName === selected.planName);
      if (!stillExists && currentPlans.length > 0) {
        // Plano selecionado não existe mais, seleciona o primeiro disponível
        this.selectPlan(currentPlans[0]);
      }
    }
  }

  validateWizardStep(stepIndex: number): boolean {
    switch (stepIndex) {
      case 0: return !!this.selectedPlan();
      case 1: return !!this.selectedSearchType();
      case 2: return true;
      default: return false;
    }
  }

  onWizardComplete(): void {
    console.log('Wizard completed - ready to search');
  }

  async resetWizard(): Promise<void> {
    const resetSteps = this.wizardState().steps.map(step => ({
      ...step,
      isValid: false,
      isComplete: false
    }));
    
    this.wizardState.set({
      currentStep: 0,
      isLoading: true,
      allPlans: [],
      steps: resetSteps
    });
    
    await this.planStatuseService.loadForCurrentUser();
    this.cdr.detectChanges();
  }

  onPlanSelected(planName: string): void {
    const plans = this.allPlanStatuses();
    const plan = plans?.find(p => p.planName === planName);
    if (plan) {
      this.selectPlan(plan);
    }
  }

  getSelectedSearchTypeLabel(): string {
    const searchType = this.selectedSearchType();
    if (!searchType) return '';
    const option = this.formTypeOptions.find(o => o.key === searchType);
    return option?.label || '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.planStatusesSub?.unsubscribe();
  }
}
